import React, { useState, useEffect, useCallback } from 'react';
import api, { API_URL } from '../../services/api';
import {
  ArrowLeft, Scale, LayoutGrid, ClipboardList, RefreshCw, Check, Sprout, Trash2
} from 'lucide-react';

// Module COMPTE MÉTAL ATELIER — front (Lot 4, 18/06/2026).
// Écran établi mobile-first (saisie) + dashboard mensuel de perte par poste.
// Backend : /api/metal (gated couts_required). Module autonome, aucun stock or.

const ACTIONS = [
  { key: 'entree',         label: 'Entrée',           cls: 'bg-green-600 hover:bg-green-700',   badge: 'bg-green-100 text-green-800' },
  { key: 'sortie',         label: 'Sortie',           cls: 'bg-blue-600 hover:bg-blue-700',     badge: 'bg-blue-100 text-blue-800' },
  { key: 'limaille',       label: 'Limaille',         cls: 'bg-amber-500 hover:bg-amber-600',   badge: 'bg-amber-100 text-amber-800' },
  { key: 'pesee_fin_mois', label: 'Pesée fin de mois', cls: 'bg-purple-600 hover:bg-purple-700', badge: 'bg-purple-100 text-purple-800' },
  { key: 'ajustement',     label: 'Ajustement',       cls: 'bg-gray-500 hover:bg-gray-600',     badge: 'bg-gray-100 text-gray-700' },
];
const ACTION_BY_KEY = Object.fromEntries(ACTIONS.map(a => [a.key, a]));
const CHIPS = ['OR JAUNE', 'OR BLANC', 'CHUTE', 'SOUDURE', 'LIMAILLE'];
const MOIS_NOMS = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

// Arbre de fonte — 5 embases (poids fixes, surchargeables). Densité/masselotte
// = défauts serveur (15.6 / 30), pas demandés à l'écran.
const EMBASES = [
  { num: 1, poids: 202.51 },
  { num: 2, poids: 202.07 },
  { num: 3, poids: 143.64 },
  { num: 4, poids: 130.00 },
  { num: 5, poids: 149.06 },
];
const DENSITE_18K = 15.6;
const MASSELOTTE_G = 30;

// Pastille écart arbre : |écart| g — vert ≤15, orange ≤40, rouge au-delà.
const ecartClass = (e) => {
  if (e == null) return 'text-gray-400';
  const a = Math.abs(e);
  if (a > 40) return 'text-red-700 font-bold';
  if (a > 15) return 'text-amber-600 font-semibold';
  return 'text-green-700';
};

const toast = (type, msg) => {
  if (window.toast && window.toast[type]) window.toast[type](msg);
  else if (type === 'error') alert(msg);
};
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fmt = (v) => (v == null || isNaN(v)) ? '—' : parseFloat(v).toFixed(3);

// Pastille % perte : vert <3, orange 3–10, rouge >10 (pattern ERP)
const pctClass = (p) => {
  if (p == null) return 'text-gray-400';
  if (p > 10) return 'text-red-700 font-bold';
  if (p >= 3) return 'text-amber-600 font-semibold';
  return 'text-green-700';
};

function CompteMetal() {
  const [vue, setVue] = useState('saisie'); // 'saisie' | 'dashboard'
  const [postes, setPostes] = useState([]);
  const [loadingPostes, setLoadingPostes] = useState(true);

  // Saisie
  const [poste, setPoste] = useState(null);     // poste sélectionné
  const [action, setAction] = useState(null);   // action sélectionnée
  const [poids, setPoids] = useState('');
  const [libelle, setLibelle] = useState('');
  const [date, setDate] = useState(todayStr());
  const [observation, setObservation] = useState('');
  const [saving, setSaving] = useState(false);
  const [histo, setHisto] = useState([]);

  // Dashboard
  const now = new Date();
  const [mois, setMois] = useState(now.getMonth() + 1);
  const [annee, setAnnee] = useState(now.getFullYear());
  const [dash, setDash] = useState(null);
  const [loadingDash, setLoadingDash] = useState(false);

  // Arbre de fonte
  const [arbEmbase, setArbEmbase] = useState(null);     // numéro d'embase sélectionné
  const [arbPoidsEmbase, setArbPoidsEmbase] = useState('');
  const [arbPoidsArbre, setArbPoidsArbre] = useState('');
  const [arbRef, setArbRef] = useState('');
  const [arbDate, setArbDate] = useState(todayStr());
  const [arbNotes, setArbNotes] = useState('');
  const [arbSaving, setArbSaving] = useState(false);
  const [arbres, setArbres] = useState([]);
  const [reelInputs, setReelInputs] = useState({});     // {arbreId: valeur saisie}

  const loadPostes = useCallback(async () => {
    setLoadingPostes(true);
    try {
      const r = await api.get(`${API_URL}/metal/postes`);
      setPostes(r.data || []);
    } catch (e) {
      toast('error', 'Erreur chargement des postes');
    } finally {
      setLoadingPostes(false);
    }
  }, []);

  const loadHisto = useCallback(async (posteId) => {
    try {
      const r = await api.get(`${API_URL}/metal/postes/${posteId}/mouvements?limit=20`);
      setHisto(r.data || []);
    } catch (e) { setHisto([]); }
  }, []);

  const loadDashboard = useCallback(async () => {
    setLoadingDash(true);
    try {
      const r = await api.get(`${API_URL}/metal/dashboard?annee=${annee}&mois=${mois}`);
      setDash(r.data);
    } catch (e) {
      toast('error', 'Erreur chargement du dashboard');
      setDash(null);
    } finally {
      setLoadingDash(false);
    }
  }, [annee, mois]);

  const loadArbres = useCallback(async () => {
    try {
      const r = await api.get(`${API_URL}/metal/arbres?limit=50`);
      setArbres(r.data || []);
    } catch (e) { setArbres([]); }
  }, []);

  useEffect(() => { loadPostes(); }, [loadPostes]);
  useEffect(() => { if (vue === 'dashboard') loadDashboard(); }, [vue, loadDashboard]);
  useEffect(() => { if (vue === 'arbre') loadArbres(); }, [vue, loadArbres]);

  // ── Arbre de fonte : live + actions ───────────────────────────────────────
  const choisirEmbase = (e) => { setArbEmbase(e.num); setArbPoidsEmbase(String(e.poids)); };
  const resetArbre = () => {
    setArbEmbase(null); setArbPoidsEmbase(''); setArbPoidsArbre('');
    setArbRef(''); setArbNotes(''); setArbDate(todayStr());
  };
  const pEmbase = parseFloat(arbPoidsEmbase);
  const pArbre = parseFloat(arbPoidsArbre);
  const resineLive = (!isNaN(pEmbase) && !isNaN(pArbre)) ? pArbre - pEmbase : null;
  const orLive = (resineLive != null && resineLive > 0)
    ? resineLive * DENSITE_18K + MASSELOTTE_G : null;

  const enregistrerArbre = async () => {
    if (isNaN(pEmbase) || isNaN(pArbre)) { toast('error', 'Saisir poids embase et poids arbre'); return; }
    if (resineLive == null || resineLive <= 0) { toast('error', 'Arbre ≤ embase, vérifie la pesée'); return; }
    setArbSaving(true);
    try {
      await api.post(`${API_URL}/metal/arbres`, {
        date_preparation: arbDate,
        reference: arbRef || undefined,
        embase_numero: arbEmbase || undefined,
        poids_embase: pEmbase,
        poids_arbre_embase: pArbre,
        notes: arbNotes || undefined,
      });
      toast('success', 'Préparation enregistrée');
      resetArbre();
      await loadArbres();
    } catch (e) {
      toast('error', e?.response?.data?.error || 'Erreur à l\'enregistrement');
    } finally {
      setArbSaving(false);
    }
  };

  const saisirReel = async (id) => {
    const v = parseFloat(reelInputs[id]);
    if (isNaN(v)) { toast('error', 'Saisir le poids réel'); return; }
    try {
      await api.put(`${API_URL}/metal/arbres/${id}/reel`, { or_reel: v });
      setReelInputs(prev => { const c = { ...prev }; delete c[id]; return c; });
      toast('success', 'Poids réel enregistré');
      await loadArbres();
    } catch (e) {
      toast('error', e?.response?.data?.error || 'Erreur');
    }
  };

  const supprimerArbre = async (id) => {
    if (!window.confirm('Supprimer cette préparation ?')) return;
    try {
      await api.delete(`${API_URL}/metal/arbres/${id}`);
      toast('success', 'Préparation supprimée');
      await loadArbres();
    } catch (e) {
      toast('error', e?.response?.data?.error || 'Erreur');
    }
  };

  const choisirPoste = (p) => { setPoste(p); setAction(null); resetForm(); loadHisto(p.id); };
  const retourPostes = () => { setPoste(null); setAction(null); resetForm(); };
  const choisirAction = (a) => { setAction(a); resetForm(); };
  const resetForm = () => { setPoids(''); setLibelle(''); setObservation(''); setDate(todayStr()); };

  const tare = poste ? parseFloat(poste.tare_boite || 0) : 0;
  const isPesee = action === 'pesee_fin_mois';
  const net = isPesee && poids !== '' && !isNaN(parseFloat(poids))
    ? parseFloat(poids) - tare : null;

  const valider = async () => {
    if (!poste || !action) return;
    if (poids === '' || isNaN(parseFloat(poids))) { toast('error', 'Saisir un poids'); return; }
    if (isPesee && net != null && net < 0) { toast('error', 'Tare supérieure au poids brut — saisie incohérente'); return; }
    setSaving(true);
    try {
      const body = {
        poste_metal_id: poste.id,
        date_mouvement: date,
        action,
        libelle: libelle || undefined,
        observation: observation || undefined,
      };
      if (isPesee) body.poids_brut = parseFloat(poids);
      else body.poids = parseFloat(poids);
      await api.post(`${API_URL}/metal/mouvements`, body);
      toast('success', `${ACTION_BY_KEY[action].label} enregistrée`);
      resetForm();
      setAction(null);
      await loadHisto(poste.id);
    } catch (e) {
      toast('error', e?.response?.data?.error || 'Erreur à l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // ── Rendu ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Toggle Saisie / Dashboard */}
      <div className="bg-white rounded-lg shadow-md p-1 flex gap-1 max-w-xl">
        <button onClick={() => setVue('saisie')}
          className={`flex-1 px-3 py-2.5 rounded-lg font-semibold flex items-center justify-center gap-1.5 text-sm transition-all ${
            vue === 'saisie' ? 'bg-gradient-to-r from-yellow-600 to-amber-600 text-white shadow' : 'text-gray-600 hover:bg-gray-100'}`}>
          <ClipboardList className="h-4 w-4" /> Saisie
        </button>
        <button onClick={() => setVue('dashboard')}
          className={`flex-1 px-3 py-2.5 rounded-lg font-semibold flex items-center justify-center gap-1.5 text-sm transition-all ${
            vue === 'dashboard' ? 'bg-gradient-to-r from-yellow-600 to-amber-600 text-white shadow' : 'text-gray-600 hover:bg-gray-100'}`}>
          <LayoutGrid className="h-4 w-4" /> Dashboard
        </button>
        <button onClick={() => setVue('arbre')}
          className={`flex-1 px-3 py-2.5 rounded-lg font-semibold flex items-center justify-center gap-1.5 text-sm transition-all ${
            vue === 'arbre' ? 'bg-gradient-to-r from-yellow-600 to-amber-600 text-white shadow' : 'text-gray-600 hover:bg-gray-100'}`}>
          <Sprout className="h-4 w-4" /> Arbre de fonte
        </button>
      </div>

      {/* ===================== VUE SAISIE ===================== */}
      {vue === 'saisie' && (
        <div className="space-y-4">
          {loadingPostes ? (
            <div className="text-center py-12 text-gray-400"><RefreshCw className="h-6 w-6 mx-auto animate-spin" /></div>
          ) : !poste ? (
            // Grille de postes
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {postes.map(p => (
                <button key={p.id} onClick={() => choisirPoste(p)}
                  className="flex flex-col items-center justify-center gap-1 py-6 px-3 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-amber-400 hover:shadow-md active:scale-95 transition-all">
                  <Scale className="h-7 w-7 text-amber-500" />
                  <span className="font-bold text-gray-800 text-center leading-tight">{p.libelle_affiche}</span>
                  {parseFloat(p.tare_boite || 0) > 0 && (
                    <span className="text-[11px] text-gray-400">tare {fmt(p.tare_boite)} g</span>
                  )}
                </button>
              ))}
              {postes.length === 0 && <div className="col-span-full text-center text-gray-400 py-8">Aucun poste actif</div>}
            </div>
          ) : (
            // Écran du poste
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <button onClick={retourPostes} className="p-2 rounded-lg hover:bg-gray-100"><ArrowLeft className="h-5 w-5" /></button>
                <h2 className="text-xl font-bold text-gray-900">{poste.libelle_affiche}</h2>
                {parseFloat(poste.tare_boite || 0) > 0 && (
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">tare {fmt(poste.tare_boite)} g</span>
                )}
              </div>

              {/* 5 actions */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {ACTIONS.map(a => (
                  <button key={a.key} onClick={() => choisirAction(a.key)}
                    className={`py-4 px-2 rounded-xl text-white font-semibold text-sm shadow active:scale-95 transition-all ${a.cls} ${action === a.key ? 'ring-4 ring-offset-1 ring-amber-300' : ''}`}>
                    {a.label}
                  </button>
                ))}
              </div>

              {/* Mini-formulaire */}
              {action && (
                <div className="bg-white border rounded-xl p-4 space-y-3 shadow-sm">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {isPesee ? 'Poids brut (boîte pleine) — g' : 'Poids (g)'}
                    </label>
                    <input
                      type="text" inputMode="decimal" autoFocus onFocus={e => e.target.select()}
                      value={poids}
                      onChange={e => setPoids(e.target.value.replace(',', '.'))}
                      placeholder="0.000"
                      className="w-full px-4 py-3 text-2xl font-bold border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                    {isPesee && (
                      <p className="mt-1 text-sm text-gray-600">
                        Net = brut − tare ({fmt(tare)} g) = <span className={`font-bold ${net != null && net < 0 ? 'text-red-600' : 'text-gray-900'}`}>{net != null ? fmt(net) : '—'}</span> g
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Libellé</label>
                    <input type="text" value={libelle} onChange={e => setLibelle(e.target.value)}
                      placeholder="ex : OR JAUNE, FAB 156…"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500" />
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {CHIPS.map(c => (
                        <button key={c} type="button" onClick={() => setLibelle(c)}
                          className="text-xs px-2.5 py-1 bg-gray-100 hover:bg-amber-100 hover:text-amber-800 border border-gray-200 rounded-full transition-colors">
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input type="date" value={date} onChange={e => setDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500" />
                  </div>

                  <div className="flex gap-2 pt-1">
                    <button onClick={valider} disabled={saving}
                      className="flex-1 px-4 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-semibold disabled:opacity-50 flex items-center justify-center gap-2 transition-all">
                      <Check className="h-5 w-5" /> {saving ? 'Enregistrement…' : 'Valider'}
                    </button>
                    <button onClick={() => setAction(null)} disabled={saving}
                      className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-all">
                      Annuler
                    </button>
                  </div>
                </div>
              )}

              {/* Historique récent du poste */}
              <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
                <div className="px-4 py-2.5 bg-gray-50 border-b text-sm font-semibold text-gray-600">Historique récent</div>
                {histo.length === 0 ? (
                  <div className="px-4 py-6 text-center text-gray-400 text-sm">Aucun mouvement</div>
                ) : (
                  <div className="divide-y">
                    {histo.map(m => {
                      const a = ACTION_BY_KEY[m.action] || { label: m.action, badge: 'bg-gray-100 text-gray-700' };
                      return (
                        <div key={m.id} className="px-4 py-2.5 flex items-center gap-3 text-sm">
                          <span className="text-gray-400 w-20 flex-shrink-0">{m.date_mouvement}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${a.badge}`}>{a.label}</span>
                          <span className="font-semibold text-gray-900 w-20 text-right flex-shrink-0">{fmt(m.poids)} g</span>
                          <span className="text-gray-500 truncate flex-1">{m.libelle || ''}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===================== VUE DASHBOARD ===================== */}
      {vue === 'dashboard' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <select value={mois} onChange={e => setMois(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg">
              {MOIS_NOMS.slice(1).map((n, i) => <option key={i + 1} value={i + 1}>{n}</option>)}
            </select>
            <input type="number" value={annee} onChange={e => setAnnee(parseInt(e.target.value) || annee)}
              className="px-3 py-2 border border-gray-300 rounded-lg w-24" />
            <button onClick={loadDashboard} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-1.5 text-sm font-medium">
              <RefreshCw className="h-4 w-4" /> Actualiser
            </button>
          </div>

          <p className="text-xs text-gray-400">
            Un mois sans pesée de boîte affiche une perte maximale (normal tant que la boîte n'est pas pesée).
          </p>

          <div className="bg-white rounded-lg shadow-md overflow-x-auto">
            {loadingDash ? (
              <div className="text-center py-12 text-gray-400"><RefreshCw className="h-6 w-6 mx-auto animate-spin" /></div>
            ) : (
              <table className="min-w-full text-sm" style={{ minWidth: '760px' }}>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2.5 text-left font-medium text-gray-500 uppercase text-xs">Poste</th>
                    <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase text-xs">À-nouveau</th>
                    <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase text-xs">Entrées</th>
                    <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase text-xs">Sorties</th>
                    <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase text-xs">Limailles</th>
                    <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase text-xs">Pesée fin</th>
                    <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase text-xs">Perte</th>
                    <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase text-xs">% perte</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(dash?.postes || []).map(l => (
                    <tr key={l.poste_id} className="hover:bg-gray-50">
                      <td className="px-3 py-2.5 font-medium text-gray-800">{l.libelle}</td>
                      <td className="px-3 py-2.5 text-right text-gray-600">{fmt(l.a_nouveau)}</td>
                      <td className="px-3 py-2.5 text-right text-gray-600">{fmt(l.entrees)}</td>
                      <td className="px-3 py-2.5 text-right text-gray-600">{fmt(l.sorties)}</td>
                      <td className="px-3 py-2.5 text-right text-gray-600">{fmt(l.limailles)}</td>
                      <td className="px-3 py-2.5 text-right text-gray-600">{fmt(l.pesee_fin)}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-gray-900">{fmt(l.perte)}</td>
                      <td className={`px-3 py-2.5 text-right ${pctClass(l.pct_perte)}`}>
                        {l.pct_perte == null ? '—' : `${l.pct_perte.toFixed(2)} %`}
                      </td>
                    </tr>
                  ))}
                  {dash?.total && (
                    <tr className="bg-gray-50 font-bold border-t-2 border-gray-300">
                      <td className="px-3 py-2.5 text-gray-900">{dash.total.libelle}</td>
                      <td className="px-3 py-2.5 text-right">{fmt(dash.total.a_nouveau)}</td>
                      <td className="px-3 py-2.5 text-right">{fmt(dash.total.entrees)}</td>
                      <td className="px-3 py-2.5 text-right">{fmt(dash.total.sorties)}</td>
                      <td className="px-3 py-2.5 text-right">{fmt(dash.total.limailles)}</td>
                      <td className="px-3 py-2.5 text-right">{fmt(dash.total.pesee_fin)}</td>
                      <td className="px-3 py-2.5 text-right">{fmt(dash.total.perte)}</td>
                      <td className={`px-3 py-2.5 text-right ${pctClass(dash.total.pct_perte)}`}>
                        {dash.total.pct_perte == null ? '—' : `${dash.total.pct_perte.toFixed(2)} %`}
                      </td>
                    </tr>
                  )}
                  {(!dash || (dash.postes || []).length === 0) && (
                    <tr><td colSpan="8" className="px-3 py-8 text-center text-gray-400">Aucune donnée</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ===================== VUE ARBRE DE FONTE ===================== */}
      {vue === 'arbre' && (
        <div className="space-y-5">
          {/* PARTIE 1 — Calculateur */}
          <div className="bg-white border rounded-xl p-4 space-y-4 shadow-sm max-w-2xl">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Sprout className="h-5 w-5 text-amber-600" /> Préparer un arbre de fonte
            </h2>

            {/* Embases */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Embase</label>
              <div className="flex flex-wrap gap-2">
                {EMBASES.map(e => (
                  <button key={e.num} type="button" onClick={() => choisirEmbase(e)}
                    className={`px-4 py-2 rounded-lg font-semibold text-sm border-2 transition-all active:scale-95 ${
                      arbEmbase === e.num
                        ? 'bg-amber-600 text-white border-amber-600'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-amber-400'}`}>
                    {e.num}
                    <span className="block text-[11px] font-normal opacity-80">{e.poids.toFixed(2)} g</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Poids embase (g)</label>
                <input type="text" inputMode="decimal" onFocus={e => e.target.select()}
                  value={arbPoidsEmbase}
                  onChange={e => setArbPoidsEmbase(e.target.value.replace(',', '.'))}
                  placeholder="ex : 202.51"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500" />
                <p className="text-[11px] text-gray-400 mt-0.5">pré-rempli par l'embase, modifiable</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Poids arbre + embase (g)</label>
                <input type="text" inputMode="decimal" autoFocus onFocus={e => e.target.select()}
                  value={arbPoidsArbre}
                  onChange={e => setArbPoidsArbre(e.target.value.replace(',', '.'))}
                  placeholder="la pesée"
                  className="w-full px-3 py-2.5 text-lg font-semibold border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500" />
              </div>
            </div>

            {/* Live */}
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4">
              {resineLive != null && resineLive <= 0 ? (
                <p className="text-red-600 font-semibold text-sm">⚠ Arbre ≤ embase, vérifie la pesée.</p>
              ) : (
                <>
                  <p className="text-sm text-gray-600">
                    Résine = {resineLive != null ? fmt(resineLive) : '—'} g
                    <span className="text-gray-400"> &nbsp;(× {DENSITE_18K} + {MASSELOTTE_G})</span>
                  </p>
                  <p className="text-2xl font-extrabold text-amber-800 mt-1">
                    Or à préparer : {orLive != null ? fmt(orLive) : '—'} g
                  </p>
                </>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Référence <span className="text-gray-400 font-normal">— optionnel</span></label>
                <input type="text" value={arbRef} onChange={e => setArbRef(e.target.value)}
                  placeholder="ex : arbre du 25/06"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input type="date" value={arbDate} onChange={e => setArbDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes <span className="text-gray-400 font-normal">— optionnel</span></label>
              <input type="text" value={arbNotes} onChange={e => setArbNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500" />
            </div>

            <div className="flex gap-2">
              <button onClick={enregistrerArbre} disabled={arbSaving || resineLive == null || resineLive <= 0}
                className="flex-1 px-4 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-semibold disabled:opacity-50 flex items-center justify-center gap-2 transition-all">
                <Check className="h-5 w-5" /> {arbSaving ? 'Enregistrement…' : 'Enregistrer cette préparation'}
              </button>
              <button onClick={resetArbre} disabled={arbSaving}
                className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-all">
                Effacer
              </button>
            </div>
          </div>

          {/* PARTIE 2 — Mes arbres */}
          <div className="bg-white rounded-lg shadow-md overflow-x-auto">
            <div className="px-4 py-2.5 bg-gray-50 border-b flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-600">Mes arbres</span>
              <button onClick={loadArbres} className="text-gray-400 hover:text-gray-600"><RefreshCw className="h-4 w-4" /></button>
            </div>
            <table className="min-w-full text-sm" style={{ minWidth: '820px' }}>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2.5 text-left font-medium text-gray-500 uppercase text-xs">Date</th>
                  <th className="px-3 py-2.5 text-left font-medium text-gray-500 uppercase text-xs">Réf</th>
                  <th className="px-3 py-2.5 text-center font-medium text-gray-500 uppercase text-xs">Embase</th>
                  <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase text-xs">Résine</th>
                  <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase text-xs">Or estimé</th>
                  <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase text-xs">Or réel</th>
                  <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase text-xs">Écart</th>
                  <th className="px-3 py-2.5 text-center font-medium text-gray-500 uppercase text-xs"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {arbres.map(a => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{a.date_preparation}</td>
                    <td className="px-3 py-2.5 text-gray-700">{a.reference || '—'}</td>
                    <td className="px-3 py-2.5 text-center text-gray-600">{a.embase_numero || '—'}</td>
                    <td className="px-3 py-2.5 text-right text-gray-600">{fmt(a.poids_resine)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-gray-900">{fmt(a.or_estime)}</td>
                    <td className="px-3 py-2.5 text-right">
                      {a.or_reel != null ? (
                        <span className="text-gray-900 font-medium">{fmt(a.or_reel)}</span>
                      ) : (
                        <div className="flex items-center gap-1 justify-end">
                          <input type="text" inputMode="decimal"
                            value={reelInputs[a.id] || ''}
                            onChange={e => setReelInputs(prev => ({ ...prev, [a.id]: e.target.value.replace(',', '.') }))}
                            placeholder="réel"
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-right" />
                          <button onClick={() => saisirReel(a.id)}
                            className="px-2 py-1 bg-amber-600 text-white rounded hover:bg-amber-700 text-xs font-semibold">OK</button>
                        </div>
                      )}
                    </td>
                    <td className={`px-3 py-2.5 text-right ${ecartClass(a.ecart)}`}>
                      {a.ecart == null ? '—' : `${a.ecart > 0 ? '+' : ''}${fmt(a.ecart)}`}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <button onClick={() => supprimerArbre(a.id)} title="Supprimer"
                        className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
                {arbres.length === 0 && (
                  <tr><td colSpan="8" className="px-3 py-8 text-center text-gray-400">Aucune préparation</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompteMetal;
